import React, { useState } from 'react';
import { Clock, Star, Heart, FlaskConical, Users, Award, ArrowLeft, ArrowRight, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
// --- Colors (mirrors brand tokens from source design) ---
const C = {
  teal: '#003841',
  tealLight: '#00252B',
  cream: '#FBECD5',
  creamLight: '#FFF9F2',
  blue: '#95CBCD',
  coral: '#E16A5F',
  coralHover: '#C95448'
};
// --- Paw Icon ---
function PawIcon({
  className = '',
  fill = 'currentColor'
}: {
  className?: string;
  fill?: string;
}) {
  return <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" className={className} fill={fill} aria-hidden="true" data-id="element-1925">
      <ellipse cx="22" cy="32" rx="10" ry="13" data-id="element-1926" />
      <ellipse cx="40" cy="20" rx="10" ry="14" data-id="element-1927" />
      <ellipse cx="60" cy="20" rx="10" ry="14" data-id="element-1928" />
      <ellipse cx="78" cy="32" rx="10" ry="13" data-id="element-1929" />
      <path d="M50 45 C 30 45, 20 60, 22 75 C 24 88, 38 92, 50 92 C 62 92, 76 88, 78 75 C 80 60, 70 45, 50 45 Z" data-id="element-1930" />
    </svg>;
}
// --- Curved Divider ---
function CurvedDivider({
  topColor,
  bottomColor,
  direction = 'down'
}: {
  topColor: string;
  bottomColor: string;
  direction?: 'up' | 'down';
}) {
  return <div className="relative w-full overflow-hidden leading-none" style={{
    backgroundColor: topColor
  }} data-id="element-1931">
      <svg className="relative block w-full h-[40px] md:h-[80px]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 100" preserveAspectRatio="none" data-id="element-1932">
        {direction === 'down' ? <path d="M0,30 Q600,90 1200,30 L1200,100 L0,100 Z" fill={bottomColor} data-id="element-1933" /> : <path d="M0,70 Q600,10 1200,70 L1200,100 L0,100 Z" fill={bottomColor} data-id="element-1934" />}
      </svg>
    </div>;
}
// --- Top Bar ---
function TopBar() {
  const items = [{
    icon: Star,
    text: 'Rated Excellent on Trustpilot'
  }, {
    icon: Heart,
    text: 'Punishment-free Method'
  }, {
    icon: FlaskConical,
    text: 'Science-based & Kind'
  }, {
    icon: Users,
    text: '15,000+ Happy Dog Parents'
  }];
  return <div className="bg-[#003841] text-white/80 py-2 px-4 overflow-hidden border-b border-white/10" data-id="element-1935">
      <div className="max-w-7xl mx-auto flex items-center justify-center gap-4 md:gap-8 flex-wrap text-xs font-medium" data-id="element-1936">
        {items.map((item, i) => <div key={i} className="flex items-center gap-1.5 whitespace-nowrap" data-id="element-1937">
            <item.icon className="w-3.5 h-3.5 text-[#95CBCD]" data-id="element-1938" />
            <span data-id="element-1939">{item.text}</span>
          </div>)}
      </div>
    </div>;
}
// --- Nav Bar ---
function NavBar() {
  return <div className="w-full px-4 pt-6 pb-2 absolute top-10 left-0 right-0 z-50 flex justify-center" data-id="element-1940">
      <div className="bg-[#FFF9F2] rounded-full px-6 py-3 flex items-center justify-between w-full max-w-5xl shadow-lg" data-id="element-1941">
        <div className="flex items-center gap-2" data-id="element-1942">
          <div className="bg-[#003841] text-white p-1.5 rounded-full w-8 h-8 flex items-center justify-center" data-id="element-1943">
            <PawIcon className="w-5 h-5" fill="#FFF9F2" data-id="element-1944" />
          </div>
          <span className="font-bold text-xl text-[#003841] tracking-tight" data-id="element-1945">
            Stressless Dogs
          </span>
        </div>

        <div className="hidden md:flex items-center gap-8 text-sm font-medium text-[#003841]/80" data-id="element-1946">
          <a href="#" className="hover:text-[#003841] transition-colors" data-id="element-1947">
            Courses
          </a>
          <a href="#" className="hover:text-[#003841] transition-colors" data-id="element-1948">
            Community
          </a>
          <a href="#" className="hover:text-[#003841] transition-colors" data-id="element-1949">
            Success Stories
          </a>
          <a href="#" className="hover:text-[#003841] transition-colors" data-id="element-1950">
            Login
          </a>
        </div>

        <button className="bg-[#E16A5F] hover:bg-[#C95448] text-white px-5 py-2.5 rounded-full text-sm font-bold transition-colors flex items-center gap-2 shadow-sm" data-id="element-1951">
          Free Webinar <PawIcon className="w-4 h-4" fill="#FFF9F2" data-id="element-1952" />
        </button>
      </div>
    </div>;
}
// --- Hero ---
function Hero() {
  const benefits = ['The 5 most common stress signals that you are probably missing right now (and why they are crucial)', 'Why traditional training (like being the "alpha") often backfires and can make your dog\'s anxiety worse.', 'A simple technique to instantly communicate more clearly with your dog, without using your voice.', 'The crucial difference between a play bow and a warning signal (this can change everything).', 'A step-by-step plan to reduce unwanted behavior (like barking or pulling) by addressing the cause, not the symptom.'];
  return <section className="bg-[#003841] relative pt-32 md:pt-48 pb-0" data-id="element-1953">
      <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-[1.2fr_1fr] gap-12 lg:gap-20 pb-20" data-id="element-1954">
        {/* Left Column */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-white" data-id="element-1955">
          <p className="text-xs font-bold tracking-wider uppercase mb-4 text-white/80" data-id="element-1956">
            Free Online Webinar: Communicating With Your Dog
          </p>
          <h1 className="text-3xl md:text-4xl lg:text-5xl leading-tight mb-8" style={{
          textWrap: 'balance' as any
        }} data-id="element-1957">
            Discover how you can use stress-free communication methods to
            prevent problems and create an unbreakable bond with your dog
          </h1>

          <p className="text-lg mb-6 text-[#FBECD5]" data-id="element-1958">
            This is what you will learn:
          </p>

          <div className="space-y-3 mb-8" data-id="element-1959">
            {benefits.map((benefit, i) => <div key={i} className="bg-[#FFF9F2] text-[#003841] rounded-2xl p-4 flex items-start gap-3 shadow-sm" data-id="element-1960">
                <div className="bg-[#003841] rounded-full p-1.5 mt-0.5 shrink-0 w-7 h-7 flex items-center justify-center" data-id="element-1961">
                  <PawIcon className="w-4 h-4" fill="#FFF9F2" data-id="element-1962" />
                </div>
                <p className="text-sm font-medium leading-snug pt-0.5" data-id="element-1963">
                  {benefit}
                </p>
              </div>)}
          </div>

          <div className="flex items-center gap-4" data-id="element-1964">
            <div className="flex -space-x-3" data-id="element-1965">
              <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=faces" alt="User" className="w-10 h-10 rounded-full border-2 border-[#003841] object-cover" data-id="element-1966" />
              <img src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=faces" alt="User" className="w-10 h-10 rounded-full border-2 border-[#003841] object-cover" data-id="element-1967" />
              <img src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=faces" alt="User" className="w-10 h-10 rounded-full border-2 border-[#003841] object-cover" data-id="element-1968" />
            </div>
            <p className="text-xs text-white/80 max-w-[200px] leading-tight" data-id="element-1969">
              More than 100,000 dog owners have already joined our webinar.
            </p>
          </div>
        </motion.div>

        {/* Right Column - Form */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="relative z-10" data-id="element-1970">
          <div className="bg-[#95CBCD]/20 backdrop-blur-sm border border-white/10 rounded-[2rem] p-8 md:p-10 text-center" data-id="element-1971">
            <h2 className="text-2xl md:text-3xl text-white mb-3" data-id="element-1972">
              Register for Webinar:
              <br data-id="element-1973" />
              Communicating with Your Dog
            </h2>
            <p className="text-sm text-[#FBECD5] mb-8 flex items-center justify-center gap-1" data-id="element-1974">
              Followed by 100,000+ owners{' '}
              <Heart className="w-4 h-4 text-[#E16A5F] fill-[#E16A5F]" data-id="element-1975" />
              <br data-id="element-1976" />
              Fill out the form below:
            </p>

            <form className="space-y-5 text-left" data-id="element-1977">
              <div data-id="element-1978">
                <label className="block text-sm font-medium text-white mb-1.5 ml-1" data-id="element-1979">
                  First name *
                </label>
                <input type="text" className="w-full px-4 py-3.5 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-[#E16A5F] transition-all" required data-id="element-1980" />
                <p className="text-[10px] text-white/50 mt-1 ml-1" data-id="element-1981">
                  * required
                </p>
              </div>
              <div data-id="element-1982">
                <label className="block text-sm font-medium text-white mb-1.5 ml-1" data-id="element-1983">
                  Email *
                </label>
                <input type="email" className="w-full px-4 py-3.5 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-[#E16A5F] transition-all" required data-id="element-1984" />
                <p className="text-[10px] text-white/50 mt-1 ml-1" data-id="element-1985">
                  * required
                </p>
              </div>

              <button type="submit" className="w-full bg-[#E16A5F] hover:bg-[#C95448] text-white font-bold py-4 rounded-xl transition-colors shadow-lg mt-4" data-id="element-1986">
                Confirm sign-up
              </button>
            </form>

            <p className="text-xs text-white/60 mt-6 leading-relaxed" data-id="element-1987">
              Everyone will receive a recording several hours afterwards.
              <br data-id="element-1988" />
              The webinar is hosted by a certified Dog Behaviorist.
            </p>
          </div>
        </motion.div>
      </div>

      {/* Peeking Dog & Divider */}
      <div className="relative" data-id="element-1989">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-[40%] z-10" data-id="element-1990">
          <div className="relative" data-id="element-1991">
            <img src="https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=200&h=200&fit=crop" alt="Peeking dog" className="w-24 h-24 rounded-full object-cover border-4 border-[#FBECD5] shadow-lg" data-id="element-1992" />
            <svg className="absolute -right-6 -bottom-2 w-12 h-12 text-[#FBECD5]" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1993">
              <path d="M20 50 Q 40 20, 60 50 T 100 50" stroke="currentColor" strokeWidth="4" fill="none" strokeLinecap="round" data-id="element-1994" />
            </svg>
          </div>
        </div>
        <CurvedDivider topColor={C.teal} bottomColor={C.cream} direction="down" data-id="element-1995" />
      </div>
    </section>;
}
// --- Familiar Section ---
function FamiliarSection() {
  return <section className="bg-[#FBECD5] relative pt-24 pb-0" data-id="element-1996">
      <div className="max-w-5xl mx-auto px-4 grid md:grid-cols-2 gap-12 items-center pb-24" data-id="element-1997">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1998">
          <img src="https://images.unsplash.com/photo-1450778869180-41d0601e046e?w=800&h=1000&fit=crop" alt="Person with dog" className="rounded-[2rem] shadow-xl w-full object-cover aspect-square md:aspect-[4/5]" data-id="element-1999" />
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        x: 20
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="text-[#003841]" data-id="element-2000">
          <h2 className="text-4xl md:text-5xl mb-6" data-id="element-2001">
            Does This Sound Familiar?
          </h2>
          <div className="space-y-4 text-lg text-[#003841]/80 leading-relaxed" data-id="element-2002">
            <p data-id="element-2003">
              You love your dog more than anything, but sometimes, it's just...
              hard. The walks that are a constant struggle. The endless barking
              that the neighbors complain about.
            </p>
            <p data-id="element-2004">
              The guilt that you're not doing it right. You may have tried
              everything, but nothing seems to work. You are not alone. And it's
              not your fault. The problem isn't a lack of love, but a lack of
              understanding.
            </p>
          </div>
        </motion.div>
      </div>

      <CurvedDivider topColor={C.cream} bottomColor={C.blue} direction="down" data-id="element-2005" />
    </section>;
}
// --- Testimonials ---
function Testimonials() {
  const testimonials = [{
    title: 'Very enthusiastic',
    text: "I've only been at it for 2 days but I already understand my dog better. Something as simple as putting on a harness now happens without resistance. By focusing more and more on his signals, the interaction keeps getting better and more fun.",
    author: 'Jessica Rodijk',
    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=faces',
    trustpilot: false
  }, {
    title: 'Dog had enough of me after 5 years',
    text: 'This course gave me new insight into my dog. I had no idea how many signals he was already giving me, and unfortunately only sought help when he started growling. By taking a different approach, I now see an enormous change - he seeks contact on his own again and sleeps relaxed next to me. That says everything.',
    author: 'Wallie Vuylsteke',
    avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=faces',
    trustpilot: true
  }, {
    title: 'You learn what real dog language is',
    text: "You learn what most dog owners don't know: how to approach dogs correctly, and what to do. I highly recommend this to anyone who wants a better relationship with their dog.",
    author: 'Suzan Muller',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=faces',
    trustpilot: false
  }];
  return <section className="bg-[#95CBCD] relative pt-20 pb-0 overflow-hidden" data-id="element-2006">
      <div className="max-w-7xl mx-auto px-4 pb-32" data-id="element-2007">
        <div className="text-center mb-12" data-id="element-2008">
          <div className="inline-flex items-center gap-1.5 bg-white/50 text-[#003841] px-4 py-1.5 rounded-full text-sm font-bold mb-6" data-id="element-2009">
            <Star className="w-4 h-4 fill-[#003841]" data-id="element-2010" /> Testimonials
          </div>
          <h2 className="text-4xl md:text-5xl text-[#003841]" data-id="element-2011">
            Loved by Over 15,000 Dog Parents
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6" data-id="element-2012">
          {testimonials.map((t, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.1
        }} className="bg-white rounded-[2rem] p-8 shadow-sm flex flex-col" data-id="element-2013">
              {t.trustpilot && <div className="flex items-center gap-2 mb-4" data-id="element-2014">
                  <div className="flex gap-0.5" data-id="element-2015">
                    {[...Array(5)].map((_, j) => <div key={j} className="bg-[#00B67A] p-1 rounded-sm" data-id="element-2016">
                        <Star className="w-3 h-3 text-white fill-white" data-id="element-2017" />
                      </div>)}
                  </div>
                  <span className="text-xs font-bold flex items-center gap-1" data-id="element-2018">
                    <Star className="w-4 h-4 text-[#00B67A] fill-[#00B67A]" data-id="element-2019" />{' '}
                    Trustpilot
                  </span>
                </div>}
              <h3 className="font-bold text-xl text-[#003841] mb-4" data-id="element-2020">
                {t.title}
              </h3>
              <p className="text-[#003841]/70 leading-relaxed mb-8 flex-grow" data-id="element-2021">
                "{t.text}"
              </p>
              <div className="flex items-center gap-3 mt-auto" data-id="element-2022">
                <img src={t.avatar} alt={t.author} className="w-10 h-10 rounded-full object-cover" data-id="element-2023" />
                <span className="font-bold text-sm text-[#003841]" data-id="element-2024">
                  {t.author}
                </span>
              </div>
            </motion.div>)}
        </div>

        <div className="flex justify-center gap-4 mt-4" data-id="element-2025">
          <button className="w-10 h-10 rounded-full bg-[#003841] text-white flex items-center justify-center hover:bg-[#00252B] transition-colors" data-id="element-2026">
            <ArrowLeft className="w-5 h-5" data-id="element-2027" />
          </button>
          <button className="w-10 h-10 rounded-full bg-[#003841] text-white flex items-center justify-center hover:bg-[#00252B] transition-colors" data-id="element-2028">
            <ArrowRight className="w-5 h-5" data-id="element-2029" />
          </button>
        </div>
      </div>

      {/* Peeking Dogs & Divider */}
      <div className="relative" data-id="element-2030">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-[30%] z-10 flex gap-4" data-id="element-2031">
          <img src="https://images.unsplash.com/photo-1561037404-61cd46aa615b?w=150&h=150&fit=crop" alt="Dog" className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover border-4 border-[#003841] shadow-lg translate-y-4" data-id="element-2032" />
          <img src="https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=150&h=150&fit=crop" alt="Dog" className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover border-4 border-[#003841] shadow-lg z-10" data-id="element-2033" />
          <img src="https://images.unsplash.com/photo-1552053831-71594a27632d?w=150&h=150&fit=crop" alt="Dog" className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover border-4 border-[#003841] shadow-lg translate-y-4" data-id="element-2034" />
        </div>
        <CurvedDivider topColor={C.blue} bottomColor={C.teal} direction="down" data-id="element-2035" />
      </div>
    </section>;
}
// --- Communicate Section ---
function CommunicateSection() {
  return <section className="bg-[#003841] py-24 px-4" data-id="element-2036">
      <div className="max-w-3xl mx-auto text-center" data-id="element-2037">
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-4xl md:text-5xl text-white mb-12 leading-tight" data-id="element-2038">
          Learn How Your Dog
          <br data-id="element-2039" />
          Communicates Naturally
        </motion.h2>

        <div className="flex justify-center gap-4 mb-12 relative" data-id="element-2040">
          <svg className="absolute left-1/2 -translate-x-[150%] top-1/2 -translate-y-1/2 w-16 h-16 text-white/20" viewBox="0 0 100 100" fill="none" data-id="element-2041">
            <path d="M10 50 Q 30 20, 50 50 T 90 50" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" data-id="element-2042" />
          </svg>
          <img src="https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=150&h=150&fit=crop" alt="Dog" className="w-20 h-20 rounded-full object-cover border-2 border-white/20" data-id="element-2043" />
          <img src="https://images.unsplash.com/photo-1552053831-71594a27632d?w=150&h=150&fit=crop" alt="Dog" className="w-20 h-20 rounded-full object-cover border-2 border-white/20" data-id="element-2044" />
          <svg className="absolute right-1/2 translate-x-[150%] top-1/2 -translate-y-1/2 w-16 h-16 text-white/20" viewBox="0 0 100 100" fill="none" data-id="element-2045">
            <path d="M10 50 Q 30 80, 50 50 T 90 50" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" data-id="element-2046" />
          </svg>
        </div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} className="bg-[#00252B]/50 border border-white/10 rounded-[2rem] p-8 md:p-12" data-id="element-2047">
          <p className="text-[#FBECD5] text-lg leading-relaxed" data-id="element-2048">
            Most dog parents don't know how dogs communicate naturally. In this
            webinar, I explain how dogs think and communicate. How they use
            their body, head, posture, and other ways to tell you what they
            think and feel, so you can understand them much better. You will
            also learn how you, as a human, can "talk" back so that your dog
            will know exactly what you are trying to say.
          </p>
        </motion.div>
      </div>
    </section>;
}
// --- About Amee ---
function AboutAmee() {
  return <section className="bg-[#003841] relative pt-12 pb-0" data-id="element-2049">
      <div className="max-w-5xl mx-auto px-4 pb-32" data-id="element-2050">
        <h2 className="text-4xl md:text-5xl text-white text-center mb-16" data-id="element-2051">
          About Amee from
          <br data-id="element-2052" />
          Stressless Dogs
        </h2>

        <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center mb-20" data-id="element-2053">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="relative" data-id="element-2054">
            <div className="bg-white p-3 pb-12 rounded-lg shadow-2xl rotate-[-2deg] max-w-sm mx-auto" data-id="element-2055">
              <img src="https://images.unsplash.com/photo-1581888227599-779811939961?w=600&h=400&fit=crop" alt="Amee with dog" className="w-full rounded object-cover aspect-[4/3]" data-id="element-2056" />
              <div className="absolute bottom-4 left-6" data-id="element-2057">
                <p className="font-bold text-[#003841] text-sm" data-id="element-2058">
                  Amee de Bruijn
                </p>
                <p className="text-xs text-[#003841]/60" data-id="element-2059">Dog Behaviorist</p>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="text-[#FBECD5] text-lg leading-relaxed" data-id="element-2060">
            <p data-id="element-2061">
              Hi, I'm Amee and I'm hosting this webinar. As a dog behavior
              expert, the well-being and happiness of a dog, as well as their
              human, is the most important thing! With our company
              StresslessDogs, we have helped over 15,000 dog parents and their
              dogs strengthen their bond and solve & prevent problems.
            </p>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center" data-id="element-2062">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="text-[#FBECD5] text-lg leading-relaxed md:order-1 order-2" data-id="element-2063">
            <p data-id="element-2064">
              I work exclusively with methods and tools that are free from
              force, pain, fear, and punishment. Themes that always play an
              important role in my work are: "trust, safety, mutual respect, and
              communication :)"
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="md:order-2 order-1" data-id="element-2065">
            <img src="https://images.unsplash.com/photo-1583337130417-3346a1be7dee?w=600&h=400&fit=crop" alt="Amee hugging dog" className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]" data-id="element-2066" />
          </motion.div>
        </div>
      </div>

      <CurvedDivider topColor={C.teal} bottomColor={C.cream} direction="down" data-id="element-2067" />
    </section>;
}
// --- FAQ ---
const faqs = [{
  question: 'How long does it take?',
  answer: 'The webinar lasts approximately 60 minutes, followed by a 15-minute live Q&A session where you can ask specific questions about your dog.'
}, {
  question: 'Can I watch it later?',
  answer: 'Yes! Everyone who registers will receive a recording link via email within a few hours after the live session ends. The recording will be available for 7 days.'
}, {
  question: 'Will something be sold?',
  answer: 'While we do mention our comprehensive courses at the end, the webinar is packed with 100% free, actionable value that you can start using immediately.'
}, {
  question: 'I have a question afterwards, can I still ask it?',
  answer: 'Absolutely. You can reply to any of our emails and our team of behaviorists will be happy to point you in the right direction.'
}];
function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return <section className="bg-[#FBECD5] relative pt-20 pb-0" data-id="element-2068">
      <div className="max-w-3xl mx-auto px-4 pb-32" data-id="element-2069">
        <div className="text-center mb-12" data-id="element-2070">
          <div className="inline-flex items-center gap-1.5 bg-white text-[#003841] px-4 py-1.5 rounded-full text-sm font-bold mb-6 shadow-sm" data-id="element-2071">
            <Star className="w-4 h-4 fill-[#003841]" data-id="element-2072" /> 14.2k
          </div>
          <h2 className="text-4xl md:text-5xl text-[#003841]" data-id="element-2073">
            Have Questions?
            <br data-id="element-2074" />
            We Have Answers.
          </h2>
        </div>

        <div className="space-y-4" data-id="element-2075">
          {faqs.map((faq, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="bg-white rounded-2xl overflow-hidden shadow-sm" data-id="element-2076">
              <button onClick={() => setOpenIndex(openIndex === i ? null : i)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-2077">
                <span className="font-bold text-[#003841] text-lg" data-id="element-2078">
                  {faq.question}
                </span>
                <div className="flex items-center gap-3" data-id="element-2079">
                  <PawIcon className="w-5 h-5 text-[#003841]/40" fill="currentColor" data-id="element-2080" />
                  <ChevronDown className={`w-5 h-5 text-[#003841] transition-transform duration-300 ${openIndex === i ? 'rotate-180' : ''}`} data-id="element-2081" />
                </div>
              </button>
              <AnimatePresence data-id="element-2082">
                {openIndex === i && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3
            }} data-id="element-2083">
                    <div className="px-6 pb-5 text-[#003841]/70 leading-relaxed" data-id="element-2084">
                      {faq.answer}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>)}
        </div>
      </div>

      {/* Peeking Dogs & Divider */}
      <div className="relative" data-id="element-2085">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-[30%] z-10 flex gap-2 md:gap-4 items-end" data-id="element-2086">
          <img src="https://images.unsplash.com/photo-1561037404-61cd46aa615b?w=150&h=150&fit=crop" alt="Dog" className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover border-4 border-[#003841] shadow-lg" data-id="element-2087" />
          <img src="https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=150&h=150&fit=crop" alt="Dog" className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover border-4 border-[#003841] shadow-lg z-10 translate-y-2" data-id="element-2088" />
          <img src="https://images.unsplash.com/photo-1552053831-71594a27632d?w=150&h=150&fit=crop" alt="Dog" className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover border-4 border-[#003841] shadow-lg" data-id="element-2089" />
          <img src="https://images.unsplash.com/photo-1517423440428-a5a00ad493e8?w=150&h=150&fit=crop" alt="Dog" className="w-14 h-14 md:w-16 md:h-16 rounded-full object-cover border-4 border-[#003841] shadow-lg -translate-y-2" data-id="element-2090" />
        </div>
        <CurvedDivider topColor={C.cream} bottomColor={C.teal} direction="down" data-id="element-2091" />
      </div>
    </section>;
}
// --- Footer ---
function FooterV2() {
  return <footer className="bg-[#003841] pt-20 pb-10 px-4 relative overflow-hidden" data-id="element-2092">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full text-center pointer-events-none opacity-5" data-id="element-2093">
        <span className="text-[15vw] font-bold text-white whitespace-nowrap" data-id="element-2094">
          Stressless Dogs
        </span>
      </div>

      <div className="max-w-6xl mx-auto relative z-10" data-id="element-2095">
        <div className="bg-[#FFF9F2] rounded-[2rem] p-8 md:p-12 mb-8 flex flex-col md:flex-row justify-between gap-12" data-id="element-2096">
          <div className="max-w-sm" data-id="element-2097">
            <div className="flex items-center gap-2 mb-6" data-id="element-2098">
              <div className="bg-[#003841] text-white p-1.5 rounded-full w-10 h-10 flex items-center justify-center" data-id="element-2099">
                <PawIcon className="w-6 h-6" fill="#FFF9F2" data-id="element-2100" />
              </div>
              <span className="font-bold text-2xl text-[#003841] tracking-tight" data-id="element-2101">
                Stressless Dogs
              </span>
            </div>
            <p className="text-[#003841]/70 text-sm leading-relaxed" data-id="element-2102">
              Explore a new way of living with your dog, based on understanding,
              communication and trust, not commands and corrections.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-12" data-id="element-2103">
            <div data-id="element-2104">
              <h4 className="font-bold text-[#003841] mb-4" data-id="element-2105">Quick Links</h4>
              <ul className="space-y-3 text-sm text-[#003841]/70" data-id="element-2106">
                <li data-id="element-2107">
                  <a href="#" className="hover:text-[#003841] transition-colors" data-id="element-2108">
                    Courses
                  </a>
                </li>
                <li data-id="element-2109">
                  <a href="#" className="hover:text-[#003841] transition-colors" data-id="element-2110">
                    Community
                  </a>
                </li>
                <li data-id="element-2111">
                  <a href="#" className="hover:text-[#003841] transition-colors" data-id="element-2112">
                    Success Stories
                  </a>
                </li>
                <li data-id="element-2113">
                  <a href="#" className="hover:text-[#003841] transition-colors" data-id="element-2114">
                    Login
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-white/50" data-id="element-2115">
          <p data-id="element-2116">© 2026 Stressless Dogs · All rights reserved</p>
          <div className="flex gap-4" data-id="element-2117">
            <a href="#" className="hover:text-white transition-colors" data-id="element-2118">
              Privacy Policy
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-2119">
              Cookie Policy
            </a>
          </div>
        </div>
      </div>
    </footer>;
}
// --- Floating CTA ---
function FloatingCTA() {
  return <div className="fixed bottom-6 right-6 z-50" data-id="element-2120">
      <button className="bg-[#E16A5F] hover:bg-[#C95448] text-white px-6 py-3 rounded-full text-sm font-bold transition-transform hover:scale-105 shadow-xl flex items-center gap-2" data-id="element-2121">
        Register now <PawIcon className="w-4 h-4" fill="#FFF9F2" data-id="element-2122" />
      </button>
    </div>;
}
// --- Page ---
export function WebinarMCV2Page() {
  return <div className="min-h-screen bg-[#003841] selection:bg-[#E16A5F]/30" style={{
    fontFamily: "'League Spartan', sans-serif"
  }} data-id="element-2123">
      <TopBar data-id="element-2124" />
      <NavBar data-id="element-2125" />
      <main data-id="element-2126">
        <Hero data-id="element-2127" />
        <FamiliarSection data-id="element-2128" />
        <Testimonials data-id="element-2129" />
        <CommunicateSection data-id="element-2130" />
        <AboutAmee data-id="element-2131" />
        <FAQSection data-id="element-2132" />
      </main>
      <FooterV2 data-id="element-2133" />
      <FloatingCTA data-id="element-2134" />
    </div>;
}